// App.js - Main application component
import React, { useEffect } from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
  Link,
  useNavigate
} from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import Header from './components/Header';
import FurnitureInpainting from './features/FurnitureInpainting';
import SearchReplace from './features/SearchReplace';
import SearchRecolor from './features/SearchRecolor';
import ImageHistory from './features/ImageHistory';
import PhotoEditorPage from './features/PhotoEditor/PhotoEditorPage';
import Login from './features/auth/Login';
import Register from './features/auth/Register';
import ForgotPassword from './features/auth/ForgotPassword';
import ResetPassword from './features/auth/ResetPassword';
import VerifyEmail from './features/auth/VerifyEmail';
import UserProfile from './features/auth/UserProfile';
import { SubscriptionDashboard } from './features/subscription';
import PaymentDashboard from './components/payment/PaymentDashboard';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider } from './context/AuthContext';
import { SubscriptionProvider } from './context/SubscriptionContext';
import { ToastProvider } from './contexts/ToastContext';
import { Card, CardHeader, CardTitle, CardDescription, CardFooter } from './components/ui/card';
import { Button } from './components/ui/button';
import { FaPaintBrush, FaExchangeAlt, FaPalette, FaArrowRight, FaHistory, FaEdit } from 'react-icons/fa';
import './styles/theme.css';
import SubscriptionSuccess from './features/subscription/SubscriptionSuccess';
import SubscriptionCancel from './features/subscription/SubscriptionCancel';
import CreditSuccess from './features/subscription/CreditSuccess';
import CreditCancel from './features/subscription/CreditCancel';

// Layout component with Header
const Layout = () => {
  return (
    <div className="flex flex-col min-h-screen bg-background dark:bg-background-dark text-text dark:text-text transition-colors duration-300 bg-subtle-texture">
      <Header />
        <main className="flex-1 p-5 w-full">
        <Outlet />
      </main>
    </div>
  );
};

// Home page component - Extracted outside App component to avoid re-creation on renders
const HomePage = () => {
  const { t, i18n } = useTranslation();
  const [, forceUpdate] = React.useReducer(x => x + 1, 0);
  const navigate = useNavigate();
  
  // Force re-render when language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      forceUpdate();
    };
    
    i18n.on('languageChanged', handleLanguageChange);
    
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n, forceUpdate]);
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3,
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 260,
        damping: 20
      }
    }
  };

  const titleVariants = {
    hidden: { opacity: 0, y: -30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 200,
        damping: 20,
        delay: 0.2
      }
    }
  };
  
  const featureCards = [
    {
      title: t('features.furnitureInpainting.title'),
      description: t('features.furnitureInpainting.description'),
      icon: <FaPaintBrush className="text-primary text-xl" />,
      path: '/furniture-inpainting',
      color: 'primary',
    },
    {
      title: t('features.searchReplace.title'),
      description: t('features.searchReplace.description'),
      icon: <FaExchangeAlt className="text-secondary text-xl" />,
      path: '/search-replace',
      color: 'secondary',
    },
    {
      title: t('features.searchRecolor.title'),
      description: t('features.searchRecolor.description'),
      icon: <FaPalette className="text-accent text-xl" />,
      path: '/search-recolor',
      color: 'accent',
    },
    {
      title: t('features.imageHistory.title', 'Image History'),
      description: t('features.imageHistory.description', 'View and manage your uploaded and generated images'),
      icon: <FaHistory className="text-info text-xl" />,
      path: '/image-history',
      color: 'info',
    },
  ];
  
  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="text-center py-10"
      key={i18n.language} // Add key to force re-render when language changes
    >
      <motion.div
        variants={titleVariants}
        className="mb-12"
      >
        <h1 className="text-4xl md:text-5xl font-bold text-gradient-primary mb-4">{t('home.title')}</h1>
        <p className="text-xl text-text-light dark:text-text-light max-w-2xl mx-auto">
          {t('home.subtitle')}
        </p>
      </motion.div>
      
      <motion.div
        className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto"
        variants={containerVariants}
      >
        {featureCards.map((card, index) => (
          <motion.div
            key={card.title}
            variants={itemVariants}
            whileHover={{
              y: -10,
              transition: { type: "spring", stiffness: 300, damping: 10 }
            }}
            onClick={() => navigate(card.path)}
            className="cursor-pointer"
          >
            <Card
              className="h-full"
              variant="glass"
            >
              <CardHeader className={`text-${card.color}`}>
                <div className="flex justify-center mb-4">
                  <div className={`w-12 h-12 rounded-full bg-${card.color}/10 flex items-center justify-center`}>
                    {card.icon}
                  </div>
                </div>
                <CardTitle className="text-xl font-bold">{card.title}</CardTitle>
                <CardDescription className="text-text-light dark:text-text-light">
                  {card.description}
                </CardDescription>
              </CardHeader>
              <CardFooter className="flex justify-center">
                <Button
                  variant="outline"
                  className={`group hover:bg-${card.color}/10 border-${card.color}/30 hover:border-${card.color}`}
                >
                  {t('common.tryItNow')}
                  <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </CardFooter>
            </Card>
          </motion.div>
        ))}
      </motion.div>
    </motion.div>
  );
};

function App() {
  const { t } = useTranslation();

  // Create router with future flags enabled
  const router = createBrowserRouter([
    {
      path: "/",
      element: <Layout />,
      children: [
        {
          index: true,
          element: <HomePage />,
        },
        {
          path: "login",
          element: <Login />,
        },
        {
          path: "register",
          element: <Register />,
        },
        {
          path: "forgot-password",
          element: <ForgotPassword />,
        },
        {
          path: "reset-password",
          element: <ResetPassword />,
        },
        {
          path: "verify-email",
          element: <VerifyEmail />,
        },
        {
          path: "profile",
          element: (
            <ProtectedRoute>
              <UserProfile />
            </ProtectedRoute>
          ),
        },
        {
          path: "subscription/*",
          element: (
            <ProtectedRoute>
              <SubscriptionDashboard />
            </ProtectedRoute>
          ),
        },
        {
          path: "subscription/success",
          element: (
            <ProtectedRoute>
              <SubscriptionSuccess />
            </ProtectedRoute>
          ),
        },
        {
          path: "subscription/cancel",
          element: (
            <ProtectedRoute>
              <SubscriptionCancel />
            </ProtectedRoute>
          ),
        },
        {
          path: "credits/success",
          element: (
            <ProtectedRoute>
              <CreditSuccess />
            </ProtectedRoute>
          ),
        },
        {
          path: "credits/cancel",
          element: (
            <ProtectedRoute>
              <CreditCancel />
            </ProtectedRoute>
          ),
        },
        {
          path: "payment/*",
          element: (
            <ProtectedRoute>
              <PaymentDashboard />
            </ProtectedRoute>
          ),
        },
        {
          path: "furniture-inpainting/*",
          element: (
            <ProtectedRoute>
              <FurnitureInpainting />
            </ProtectedRoute>
          ),
        },
        {
          path: "search-replace/*",
          element: (
            <ProtectedRoute>
              <SearchReplace />
            </ProtectedRoute>
          ),
        },
        {
          path: "search-recolor/*",
          element: (
            <ProtectedRoute>
              <SearchRecolor />
            </ProtectedRoute>
          ),
        },
        {
          path: "image-history/*",
          element: (
            <ProtectedRoute>
              <ImageHistory />
            </ProtectedRoute>
          ),
        },
        {
          path: "photo-editor/:imageId?",
          element: (
            <ProtectedRoute>
              <PhotoEditorPage />
            </ProtectedRoute>
          ),
        },
        {
          path: "*",
          element: <Navigate to="/" replace />,
        },
      ],
    },
  ], {
    future: {
      v7_normalizeFormMethod: true,
    },
  });

  return (
    <ToastProvider>
      <AuthProvider>
        <SubscriptionProvider>
          <RouterProvider router={router} />
        </SubscriptionProvider>
      </AuthProvider>
    </ToastProvider>
  );
}

export default App;
